<script setup>
/**
 * @fileoverview Floating zoomed-in view of a glaze photo, shown beside a
 * hovered (or keyboard-focused) swatch so shoppers can see the glaze's real
 * texture and variation.
 *
 * Shows the 400px Shopify image immediately, then swaps in the full
 * resolution original (utils/glazeImages.js) once it loads. The original is
 * displayed ~1100 CSS px wide and centered, so the window shows the middle of
 * the photo at near-native detail regardless of the original's size. If the
 * original fails, the Shopify image stays (zoomed modestly so it stays sharp).
 *
 * Teleported to <body> and fixed-positioned so the scrolling sidebar can't
 * clip it.
 *
 * @component
 */
import { ref, computed, watch } from 'vue';
import { highResGlazeUrl, loadImageSize } from '../utils/glazeImages.js';

const props = defineProps({
    name: { type: String, required: true },
    imageUrl: { type: String, required: true },
    /** Bounding rect of the hovered swatch */
    anchorRect: { type: Object, required: true },
});

const SIZE = 280;
const GAP = 16;
const MARGIN = 8;
const CAPTION_HEIGHT = 44;
/** Width (CSS px) the high-res original is drawn at; the window shows its center */
const DETAIL_WIDTH = 1100;
/** Zoom for the 400px fallback, kept low so it doesn't blur */
const FALLBACK_ZOOM = 1.5;

const original = computed(() => highResGlazeUrl(props.imageUrl));
/** { url, width, height } once the original has loaded */
const loaded = ref(null);

watch(
    original,
    (url) => {
        loaded.value = null;
        if (!url) return;
        loadImageSize(url).then(
            (size) => {
                if (original.value === url) loaded.value = { url, ...size };
            },
            () => {
                // Keep the Shopify image
            },
        );
    },
    { immediate: true },
);

const imageStyle = computed(() => {
    const hi = loaded.value;
    if (hi) {
        // ~DETAIL_WIDTH wide (never upscaled), but always enough to fill the window
        const cover = Math.max(SIZE, (SIZE * hi.width) / hi.height);
        const width = Math.max(cover, Math.min(DETAIL_WIDTH, hi.width));
        return { backgroundImage: `url("${hi.url}")`, backgroundSize: `${width}px auto` };
    }
    return {
        backgroundImage: `url("${props.imageUrl}")`,
        backgroundSize: `auto ${FALLBACK_ZOOM * 100}%`,
    };
});

const position = computed(() => {
    const r = props.anchorRect;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prefer the right of the swatch (over the preview canvas), else the left
    let left = r.right + GAP;
    if (left + SIZE > vw - MARGIN) left = Math.max(MARGIN, r.left - GAP - SIZE);
    const height = SIZE + CAPTION_HEIGHT;
    const top = Math.min(
        Math.max(MARGIN, r.top + r.height / 2 - SIZE / 2),
        Math.max(MARGIN, vh - height - MARGIN),
    );
    return { left: `${left}px`, top: `${top}px`, width: `${SIZE}px` };
});
</script>

<template>
    <Teleport to="body">
        <div
            class="fixed z-[1000] pointer-events-none bg-white border border-dryline shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
            :style="position"
            data-testid="glaze-zoom"
            aria-hidden="true"
        >
            <div
                class="w-full bg-center bg-no-repeat bg-engobe-100"
                :style="{ height: `${SIZE}px`, ...imageStyle }"
                data-testid="glaze-zoom-image"
                :data-source="loaded ? 'original' : 'shopify'"
            />
            <div class="px-3 py-2 flex items-baseline justify-between gap-2">
                <span class="text-sm font-semibold text-black truncate">{{ name }}</span>
                <span class="text-[10px] uppercase tracking-wide text-dryline shrink-0">
                    Glaze detail
                </span>
            </div>
        </div>
    </Teleport>
</template>
